import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import {
	SortDirection,
	TaskAddCommentRequest,
	TaskGroup,
	TaskSave,
} from "@/features/task/model/Task/TaskRequest";
import {
	UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

export const useCreateTask = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: createTask, isPending: isSubmitting } = useMutation({
		mutationFn: (request: TaskSave) => SATOSPortal.CreateTask(request),
		onMutate: (newTask) => {
			queryClient.setQueryData([TaskQueryKeys.tasks], newTask);
		},
		onError: (_, __, previousTask) => {
			queryClient.setQueryData([TaskQueryKeys.tasks], previousTask);
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.tasks],
			});
		},
	});
	return { createTask, isSubmitting };
};

export const useCreateTaskComment = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTaskComment, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: TaskAddCommentRequest) =>
				SATOSPortal.CreateTaskComment(request),

			onMutate: async () => {
				// Optionally cancel any outgoing refetches
				await queryClient.cancelQueries({ queryKey: [TaskQueryKeys.tasks] });

				// Optionally return rollback snapshot (for optimistic updates)
				const previousData = queryClient.getQueryData([TaskQueryKeys.tasks]);
				return previousData;
			},

			onError: (_, __, context) => {
				if (context) {
					queryClient.setQueryData([TaskQueryKeys.tasks], context);
				}
			},

			onSettled: () => {
				void queryClient.invalidateQueries({
					queryKey: [TaskQueryKeys.tasks],
				});
			},
		});

	return { createTaskComment, isSubmitting };
};

export const useUpdateTask = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTask, isPending: isSubmitting } = useMutation({
		mutationFn: ({ taskId, request }: { taskId: number; request: TaskSave }) =>
			SATOSPortal.UpdateTask(taskId, request),
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey: [TaskQueryKeys.tasks] });

			const previousTasks = queryClient.getQueryData([TaskQueryKeys.tasks]);

			return previousTasks;
		},
		onError: (_, __, context) => {
			if (context) {
				queryClient.setQueryData([TaskQueryKeys.tasks], context);
			}
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.tasks],
			});
		},
	});

	return { updateTask, isSubmitting };
};

export const useFetchTaskGroups = (): UseQueryResult<TaskGroup[]> => {
	return useQuery<TaskGroup[]>({
		queryKey: [TaskQueryKeys.groups],
		queryFn: () => SATOSPortal.GetTaskGroupsAPI(),
		staleTime: 5 * 60 * 1000, // Optional: cache for 5 minutes
		// enabled: true, // Optional: can be dynamic if needed
	});
};

export const useFetchTaskById = (taskId: number) => {
	return useQuery<unknown>({
		queryKey: [TaskQueryKeys.groups, taskId],
		queryFn: () => SATOSPortal.GetTaskSingle(taskId),
		enabled: Number.isFinite(taskId) && taskId > 0,
	});
};

export const useFetchTasks = (
	groupIds: number[],
	status: string,
	page: number,
	pageSize: number,
	searchTermTable?: string,
	sort?: SortDirection,
	enabled = true,
) => {
	const queryKey = [
		"tasks",
		groupIds.join(","),
		status,
		page,
		pageSize,
		searchTermTable ?? "",
		sort ?? "",
	] as const;
	return useQuery({
		queryKey,
		queryFn: () =>
			SATOSPortal.GetTasks(
				groupIds,
				status,
				page,
				pageSize,
				searchTermTable,
				sort,
			),
		enabled,
	});
};

export const useFetchRecurringChildTasks = (
	recurringTemplateId: number,
	page: number,
	pageSize: number,
	sort?: SortDirection,
	enabled = true,
) => {
	return useQuery({
		queryKey: [
			TaskQueryKeys.recurringChildren,
			recurringTemplateId,
			page,
			pageSize,
			sort ?? "",
		],
		queryFn: () =>
			SATOSPortal.GetTasks(
				[],
				"OwnReccuring",
				page,
				pageSize,
				undefined,
				sort,
				recurringTemplateId,
			),
		enabled:
			enabled &&
			Number.isFinite(recurringTemplateId) &&
			recurringTemplateId > 0,
	});
};

export { useFetchProcedureUpdateSuggestions } from "@/features/procedures/api/procedure.queries";

export class TaskQueryKeys {
	static readonly tasks = "tasks";
	static readonly comments = "comments";
	static readonly groups = "groups";
	static readonly pagination = "pagination";
	static readonly recurringChildren = "recurringChildren";
	static readonly procedureUpdatesSuggestions = "procedureUpdatesSuggestions";
	static readonly newImprov = "newImprov";
	static readonly myImprov = "myImprov";
	static readonly verified = "verified";
	static readonly declined = "declined";
}
